import type { ItemFieldRow } from './item-field-reader';
import { createCreatureSizeRecalculator } from './creature-size-recalculation';
import type { CreatureSizeRecalculationDependencies } from './creature-size-recalculation';
import { createCreatureShapeshiftCompiler } from './creature-shapeshift';
import type { CreatureShapeshiftDependencies } from './creature-shapeshift';
import { createVitalityCompiler } from './vitality-build-context';
import type { VitalityCompilerDependencies } from './vitality-build-context';
import { createInventoryReadContext } from './inventory-read-context';
import { createInventoryEconomyReader } from './inventory-economy-reader';
import { calculatePossessorInventoryCarryWeight } from './inventory-totals';
import { phpJsonFloat } from './php-decimal-values';
import { phpJsonTruthy } from './php-json-values';

export type CreaturePostInventoryStage = 'size' | 'shapeshift' | 'pre-vitality' | 'vitality' | 'weights';
export interface CreaturePostInventoryDependencies {
    size: Omit<CreatureSizeRecalculationDependencies, 'resolveInventoryInstance' | 'inventoryContext' | 'trace'>;
    shapeshift: Omit<CreatureShapeshiftDependencies, 'resolveInventoryInstance' | 'inventoryContext' | 'trace'>;
    vitality: Omit<VitalityCompilerDependencies, 'resolveInventoryInstance' | 'inventoryContext' | 'trace'>;
    resolveInventoryInstance: (id: string) => ItemFieldRow | null;
    trace?: (stage: CreaturePostInventoryStage, event: readonly unknown[]) => void;
    /** Diagnostic snapshots are independent copies and cannot alter compilation. */
    observeStage?: (stage: CreaturePostInventoryStage, row: ItemFieldRow, burn: boolean | null) => void;
}
function copy<T>(value: T): T {
    if (value === null || typeof value !== 'object') return value;
    return (Array.isArray(value) ? value.map(copy) : Object.fromEntries(Object.entries(value).map(([key, child]) => [key, copy(child)]))) as T;
}
/** Contiguous encounter-finalizer window after inventory grants. The actual
 * previous stage's output feeds each next stage; no intermediate row is supplied.
 * Returns the burn signal without applying any status or persistent write.
 */
export function createCreaturePostInventoryCompiler(deps: CreaturePostInventoryDependencies): (input: ItemFieldRow) => { row: ItemFieldRow; burn: boolean } {
    let stage: CreaturePostInventoryStage = 'size';
    const trace = (event: readonly unknown[]): void => { deps.trace?.(stage, event); };
    const inventoryContext = createInventoryReadContext(id => { trace(['inventory', id]); return deps.resolveInventoryInstance(id); });
    const shared = { inventoryContext, resolveInventoryInstance: deps.resolveInventoryInstance, trace };
    const size = createCreatureSizeRecalculator({ ...deps.size, ...shared });
    const shapeshift = createCreatureShapeshiftCompiler({ ...deps.shapeshift, ...shared });
    const vitality = createVitalityCompiler({ ...deps.vitality, ...shared });
    const economy = createInventoryEconomyReader({ inventoryContext, resolveInventoryInstance: deps.resolveInventoryInstance });
    const weightReaders = { computedValue: economy, layerValue: vitality.readers.scalar,
        hasLayer: (): boolean => { throw new Error('Native flat possessor carry does not read a layer list'); } };
    const observe = (row: ItemFieldRow, burn: boolean | null = null): void => { deps.observeStage?.(stage, copy(row), burn); };
    return input => {
        stage = 'size'; let row = size(input); observe(row);
        stage = 'shapeshift'; row = shapeshift(row); observe(row);
        stage = 'pre-vitality';
        const output = Object.fromEntries(Object.entries(row));
        const flag = output.shapeshifted;
        const first = typeof flag === 'string' ? flag[0] : flag !== null && typeof flag === 'object' ? (flag as Record<string, unknown>)['0'] : undefined;
        if (phpJsonTruthy(first)) { delete output.derivative_speed_basis; delete output.pre_status_speed_layer_presence; }
        row = output; observe(row);
        stage = 'vitality'; const result = vitality.compile(row); observe(result.row, result.burn);
        stage = 'weights';
        const final = Object.fromEntries(Object.entries(result.row));
        final.total_weight_held = calculatePossessorInventoryCarryWeight(final.inventory, weightReaders);
        final.gross_weight = phpJsonFloat(vitality.readers.scalar(final, 'weight') ?? 0) + phpJsonFloat(final.total_weight_held ?? 0);
        observe(final, result.burn);
        return { row: final, burn: result.burn };
    };
}
